/**
 * Detailed estimate: house basics + structure + rooms/openings on one page,
 * with typical values pre-filled so every section is optional to change.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import BathtubOutlinedIcon from '@mui/icons-material/BathtubOutlined';
import CountertopsOutlinedIcon from '@mui/icons-material/CountertopsOutlined';
import WeekendOutlinedIcon from '@mui/icons-material/WeekendOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import DoorFrontOutlinedIcon from '@mui/icons-material/DoorFrontOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import WindowOutlinedIcon from '@mui/icons-material/WindowOutlined';
import { Counter, FieldLabel, NumberField, Page, PageHeader, SectionCard, StickyActions } from '../ui';
import HouseBasicsFields, { isBasicsValid } from './HouseBasicsFields';
import { DEFAULT_QUICK_DRAFT } from './QuickEstimatePage';
import { getEstimateCopy } from './copy';
import { useLanguage } from '../../utils/LanguageContext';
import { useEstimate } from '../../contexts/EstimateContext';
import { useGate } from '../../hooks/useGate';
import { GATE_ACTIONS } from '../../constants/gateConfig';
import UsageAlertBanner from '../gate/UsageAlertBanner';
import { calculateDetailedEstimate, getDefaultSpecs } from '../../utils/estimateEngine';

const ROOM_FIELDS = ['bedrooms', 'bathrooms', 'kitchen', 'livingArea', 'store', 'mainDoors', 'roomDoors', 'windows'];

const STRUCTURE_OPTIONS = {
  wallType: ['brick45', 'brick9', 'block'],
  foundationType: ['strip', 'pad', 'raft'],
  roofType: ['metal', 'tiles', 'concrete'],
  ceilingType: ['gypsum', 'pvc', 'plaster'],
};

const buildDraft = (inputs = {}) => {
  const basics = { ...DEFAULT_QUICK_DRAFT, ...inputs };
  const specs = getDefaultSpecs(basics.houseType, Number(basics.builtUpArea) || 1200, basics.floors);
  return {
    ...specs,
    ...inputs,
    builtUpArea: inputs.builtUpArea ? String(inputs.builtUpArea) : '',
    floors: Number(basics.floors) || 1,
    finishingLevel: basics.finishingLevel,
    projectName: basics.projectName || '',
    wallHeight: String(inputs.wallHeight || specs.wallHeight),
  };
};

const ChoiceChips = ({ label, value, options, onChange, t }) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }} role="radiogroup" aria-label={label}>
      {options.map((opt) => (
        <Chip
          key={opt}
          role="radio"
          aria-checked={value === opt}
          label={t[opt]}
          onClick={() => onChange(opt)}
          color={value === opt ? 'primary' : 'default'}
          variant={value === opt ? 'filled' : 'outlined'}
          sx={{ height: 36, px: 0.5, fontSize: '0.85rem' }}
        />
      ))}
    </Stack>
  </Box>
);

const DetailedEstimatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = getEstimateCopy(language);
  const { detailedDraft, setDetailedDraft, result, setResult, setCompare } = useEstimate();
  const { checkAccess, GateModalComponent } = useGate();

  const compareMode = searchParams.get('compare') === '1' && !!result;
  const fromResult = (compareMode || searchParams.get('from') === 'result') && !!result;

  const [draft, setDraft] = useState(() => {
    if (fromResult) return { ...buildDraft(result.inputs), roomsTouched: true, foundationTouched: true };
    return detailedDraft || buildDraft();
  });
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!compareMode) setDetailedDraft(draft);
  }, [draft, compareMode, setDetailedDraft]);

  // Update basics; re-derive typical rooms/foundation until the user edits them
  const handleBasicsChange = (patch) =>
    setDraft((d) => {
      const next = { ...d, ...patch };
      const specs = getDefaultSpecs(next.houseType, Number(next.builtUpArea) || 1200, next.floors);
      if (!d.roomsTouched) ROOM_FIELDS.forEach((f) => { next[f] = specs[f]; });
      if (!d.foundationTouched) next.foundationType = specs.foundationType;
      return next;
    });

  const setField = (field, value) =>
    setDraft((d) => ({
      ...d,
      [field]: value,
      roomsTouched: d.roomsTouched || ROOM_FIELDS.includes(field),
      foundationTouched: d.foundationTouched || field === 'foundationType',
    }));

  const handleCalculate = () => {
    if (!isBasicsValid(draft)) {
      setShowErrors(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!checkAccess(GATE_ACTIONS.COMPLETE_HOME_ESTIMATE)) return;

    const { roomsTouched, foundationTouched, ...inputs } = draft;
    const estimate = calculateDetailedEstimate({
      ...inputs,
      builtUpArea: Number(inputs.builtUpArea),
      wallHeight: Number(inputs.wallHeight) || 10,
    });
    if (compareMode) {
      setCompare(estimate);
    } else {
      setResult(estimate);
    }
    navigate('/app/estimate/result');
  };

  const roomIcons = {
    bedrooms: <BedOutlinedIcon />,
    bathrooms: <BathtubOutlinedIcon />,
    kitchen: <CountertopsOutlinedIcon />,
    livingArea: <WeekendOutlinedIcon />,
    store: <WarehouseOutlinedIcon />,
    mainDoors: <DoorFrontOutlinedIcon />,
    roomDoors: <MeetingRoomOutlinedIcon />,
    windows: <WindowOutlinedIcon />,
  };

  const counter = (field, min, max, helper) => (
    <Counter
      key={field}
      label={t[field]}
      helper={helper}
      value={Number(draft[field]) || 0}
      min={min}
      max={max}
      icon={roomIcons[field]}
      onChange={(v) => setField(field, v)}
    />
  );

  return (
    <Page maxWidth="md">
      <PageHeader
        title={t.detailedTitle}
        subtitle={t.detailedDesc}
        onBack={() => navigate(fromResult ? '/app/estimate/result' : '/app')}
        backLabel={t.back}
      />

      {compareMode ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t.comparing}: <strong>{result.inputs.projectName || t.current}</strong>
        </Alert>
      ) : (
        <UsageAlertBanner actionCode={GATE_ACTIONS.COMPLETE_HOME_ESTIMATE} language={language} />
      )}

      <SectionCard title={t.sectionHouse}>
        <HouseBasicsFields
          draft={draft}
          onChange={handleBasicsChange}
          t={t}
          language={language}
          showErrors={showErrors}
          showProjectName={!compareMode}
        />
      </SectionCard>

      <SectionCard title={t.sectionStructure} subtitle={t.sectionStructureHint}>
        <Stack spacing={2.5}>
          <Box sx={{ maxWidth: 260 }}>
            <NumberField
              label={t.wallHeight}
              value={draft.wallHeight}
              onChange={(v) => setField('wallHeight', v)}
              unit="ft"
            />
          </Box>
          {Object.entries(STRUCTURE_OPTIONS).map(([field, options]) => (
            <ChoiceChips
              key={field}
              label={t[field]}
              value={draft[field]}
              options={options}
              onChange={(v) => setField(field, v)}
              t={t}
            />
          ))}
        </Stack>
      </SectionCard>

      <SectionCard title={t.sectionRooms} subtitle={t.sectionRoomsHint}>
        <Grid container columnSpacing={4}>
          <Grid item xs={12} sm={6}>
            {counter('bedrooms', 0, 10)}
            {counter('bathrooms', 0, 8)}
            {counter('kitchen', 0, 3)}
            {counter('livingArea', 0, 4)}
            {counter('store', 0, 4)}
          </Grid>
          <Grid item xs={12} sm={6}>
            {counter('mainDoors', 0, 6, "7' × 3'")}
            {counter('roomDoors', 0, 20, "7' × 2.5'")}
            {counter('windows', 0, 40, "4' × 3'")}
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              {t.bathroomDoorsNote}
            </Typography>
          </Grid>
        </Grid>
      </SectionCard>

      <StickyActions>
        <Button fullWidth size="large" variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={handleCalculate}>
          {compareMode ? t.calculateCompare : t.calculate}
        </Button>
      </StickyActions>

      <GateModalComponent />
    </Page>
  );
};

export default DetailedEstimatePage;
